<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { computed, WritableComputedRef, useAttrs } from 'vue'

import { cn } from '@/utils'

const props = withDefaults(
  defineProps<{
    defaultValue?: string | number | null
    modelValue?: string | number | File | null
    type?: string
    error?: boolean
  }>(),
  {
    type: 'text',
    error: false,
    defaultValue: null,
    modelValue: null,
  }
)

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number | File | null): void
  (e: 'change', event: Event): void
}>()

const attrs = useAttrs()

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: false,
  defaultValue: props.defaultValue ?? null,
}) as WritableComputedRef<string | number | File | null | undefined>

const onFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files

  if (files && files.length > 0) {
    modelValue.value = files[0]
  } else {
    modelValue.value = null
  }

  // Emit the change event to forward it
  emits('change', event)
}

const classes = computed(() => {
  return cn(
    'border-input ring-offset-background focus-visible:ring-ring placeholder:text-muted-foreground flex h-10 w-full rounded-md border px-3 py-2 text-sm file:cursor-pointer file:border-0 file:bg-slate-100 file:px-2 file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
    attrs.class as string,
    { 'border-red-500 text-red-500': props.error }
  )
})
</script>

<template>
  <input
    v-if="props.type !== 'file'"
    v-model="modelValue"
    :type="props.type"
    :class="classes"
    tabindex="-1"
    v-bind="attrs"
  />
  <input v-else type="file" :class="['cursor-pointer', classes]" v-bind="attrs" tabindex="-1" @change="onFileChange" />
</template>
